import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppShell,
  Center,
  Stack,
  Image,
  UnstyledButton,
  Tooltip,
} from '@mantine/core';
import { BetaIcon, Logo } from '../../../../lib/components';
import { Outlet, Link, NavLink, useLocation } from 'react-router-dom';
import {
  useBusinessInvitations,
  useResetParams,
  useUser,
} from '../../../hooks';
import { InvitationStatus } from '../../types';
import PLOTS_ICON from '../../../../assets/map.svg';
import SUPPLIERS_ICON from '../../../../assets/suppliers-icon.svg';
import BUYERS_ICON from '../../../../assets/buyer-icon.svg';
import TRANSACTIONS_ICON from '../../../../assets/transactions-icon.svg';
import classes from './main-layout.module.css';
import { useDisclosure } from '@mantine/hooks';
import Chat from '../../../pages/Chat/Chat';
import ChatIcon from '../../../../assets/chat.svg';

type Props = {
  route: string;
  tooltip: string;
};

const StyledNavLink = ({
  children,
  route,
  tooltip,
}: PropsWithChildren<Props>) => {
  const { resetParams } = useResetParams();
  return (
    <Tooltip label={tooltip} position="right">
      <NavLink
        data-testid={tooltip}
        className={({ isActive }) =>
          [classes.link, isActive ? classes.active : ''].join(' ')
        }
        to={`${route}?${resetParams()}`}
      >
        {children}
      </NavLink>
    </Tooltip>
  );
};

export const MainLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { data: notifications } = useBusinessInvitations(
    InvitationStatus.PENDING
  );

  const { data: user } = useUser();
  const canManageTransactions = user?.entitlements?.canManageTransactions;
  const canViewSurveys = user?.entitlements?.canViewSurveys;
  const [opened, { close, toggle }] = useDisclosure(false);

  const handleOpenChat = () => {
    toggle();
  };

  return (
    <AppShell withBorder={false} className={'mantine-AppShell-main'}>
      <AppShell.Navbar className={classes['nav-bar']}>
        <Chat opened={opened} close={close} />

        <Stack justify="space-between" h="100%">
          <Center mt={15}>
            <Stack>
              <UnstyledButton to="/" component={Link}>
                <Center ml={5}>
                  <Logo width={30} color="white" variant="submark" />
                </Center>
              </UnstyledButton>

              <StyledNavLink route="/map/plots" tooltip={t('nav.links.plots')}>
                <Image src={PLOTS_ICON} w={23} />
              </StyledNavLink>
              {/* TODO replace SVG's with Beta or custom icons */}
              <StyledNavLink
                route="/map/suppliers"
                tooltip={t('nav.links.suppliers')}
              >
                <Image src={SUPPLIERS_ICON} w={23} />
              </StyledNavLink>
              <StyledNavLink
                route="/map/buyers"
                tooltip={t('nav.links.buyers')}
              >
                <Image src={BUYERS_ICON} w={23} />
              </StyledNavLink>
              {canManageTransactions && (
                <StyledNavLink
                  route="/map/transactions"
                  tooltip={t('nav.links.transactions')}
                >
                  <Image src={TRANSACTIONS_ICON} w={23} />
                </StyledNavLink>
              )}
              {canViewSurveys && (
                <StyledNavLink
                  route="/map/documents"
                  tooltip={t('nav.links.documents')}
                >
                  <BetaIcon icon="file" color="white" />
                </StyledNavLink>
              )}
              <StyledNavLink
                route="/map/notifications"
                tooltip={t('nav.links.notifications')}
              >
                <div className={classes.notifications}>
                  {!!notifications?.length && <div className={classes.dot} />}
                  <BetaIcon icon="bell" color="white" />
                </div>
              </StyledNavLink>
            </Stack>
          </Center>
          <Stack align="center">
            <Tooltip label={t('chat.askAssistant')} position="right" withArrow>
              <UnstyledButton
                onClick={handleOpenChat}
                title="Ask me anything"
                m="0 0 16px 4px"
              >
                <Image src={ChatIcon} w={24} h={24} />
              </UnstyledButton>
            </Tooltip>
          </Stack>
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
};
